import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cl from 'classnames';
import { Account, Company, DevicePaymentMethodEnum } from 'src/models';
import { RoleEnum } from 'src/security/roles';
import { HEADSETS } from 'src/constants';
import { useContextData, useLanguage, usePage } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import { useAccount } from 'src/services/auth';
import { useCompanyById } from 'src/services/company';
import { useHeadsetById, saveHeadsetAction } from 'src/services/headsets';
import { PageSwitcher } from 'src/components';
import { Button, Modal } from 'src/components/general';
import styles from './headsetPaymentsStyle.module.scss';

const paymentMethodsText = (method: string) => {
  switch (method) {
    case 'payment_system_email':
      return 'page.headset.payments.paymentSystemEmail';
    case 'payment_system_mobile':
      return 'page.headset.payments.paymentSystemMobile';
    case 'room_bill':
      return 'page.headset.payments.roomBill';
    case 'mobile':
      return 'page.headset.payments.mobile';
    case 'qr_code':
      return 'page.headset.payments.qrCode';
  }
};

export const HeadsetPayments = () => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const {
    access: { accessMap },
  } = usePage();
  const {
    header: { width: headerWidth },
  } = useContextData();
  const dispatch = useAppDispatch();
  const { t, td } = useLanguage();
  const account = useAccount() as Account;
  const headset = useHeadsetById(headsetId);
  const company = useCompanyById(companyId) as Company;
  const [confirmModal, setConfirmModal] = useState<{
    method: DevicePaymentMethodEnum;
    enabled: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const paymentMethods = useMemo(
    () => headset?.paymentMethods ?? [],
    [headset?.paymentMethods],
  );

  const handleUpdateCompanyPaymentMethods = useCallback(
    async ({ method, enabled }: { method: string; enabled: boolean }) => {
      const methods = enabled
        ? [...paymentMethods.filter((cm) => cm !== method)]
        : [...paymentMethods, method];

      setIsLoading(true);

      const res = await dispatch(
        saveHeadsetAction({
          companyId,
          headsetId,
          data: {
            paymentMethods: methods,
          },
        }),
      );

      if (saveHeadsetAction.fulfilled.match(res)) {
        setConfirmModal(null);
      }

      setIsLoading(false);
    },
    [companyId, headsetId, dispatch, paymentMethods],
  );

  const mergedPaymentMethods = useMemo(() => {
    if (!company) {
      return [];
    }
    const results = company.devicePaymentMethods.map(
      (m: DevicePaymentMethodEnum) => ({
        method: m,
        disabled:
          m === DevicePaymentMethodEnum.roomBill && headset?.room === null,
        enabled: paymentMethods.includes(m),
      }),
    );
    if (![RoleEnum.admin, RoleEnum.engineer].includes(account.role)) {
      return results.filter((r) => r.enabled);
    }
    return results;
  }, [company, paymentMethods, headset?.room, account.role]);

  const confirmText = useMemo(() => {
    if (!confirmModal) {
      return null;
    }

    const method = td(paymentMethodsText(confirmModal.method));

    return t(
      confirmModal.enabled
        ? 'page.headset.payments.removeMethodConfirm'
        : 'page.headset.payments.addMethodConfirm',
      { method },
    );
  }, [confirmModal, t, td]);

  return (
    <div className={styles.container}>
      <Modal
        header={t(
          confirmModal?.enabled
            ? 'page.headset.payments.removeMethod'
            : 'page.headset.payments.addMethod',
        )}
        show={Boolean(confirmModal)}
        close={() => {
          setConfirmModal(null);
        }}
        className={styles.confirmModal}
        footer={
          <div className={styles.confirmModalButtons}>
            <Button
              label={t('general.cancel')}
              onClick={() => {
                setConfirmModal(null);
              }}
              design="outlineBlue"
              autoSize
              className={styles.closeCancelSubsBtn}
              disabled={isLoading}
            />
            <Button
              label={t(
                confirmModal?.enabled ? 'general.delete' : 'general.add',
              )}
              onClick={() => {
                if (confirmModal) {
                  handleUpdateCompanyPaymentMethods(confirmModal);
                }
              }}
              design={confirmModal?.enabled ? 'pink' : 'lightBlue'}
              autoSize
              className={styles.cancelSubsModalBtn}
              disabled={isLoading}
            />
          </div>
        }
        closeOnEsc
      >
        <div className={styles.confirmModalContent}>{confirmText}</div>
      </Modal>
      <div
        className={styles.topSection}
        style={{ minWidth: headerWidth, maxWidth: headerWidth }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HEADSETS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
        </div>
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.wrapper}>
          <div className={styles.table}>
            <div className={styles.headerCell}>
              <div className={styles.header}>
                {t('page.headset.payments.paymentMethod')}
              </div>
            </div>
            <div className={styles.headerCell}>
              <div className={styles.header}>
                {t('page.headset.payments.actions')}
              </div>
            </div>
            {mergedPaymentMethods.map(({ method, enabled, disabled }) => (
              <Fragment key={`m-${method}`}>
                <div className={styles.cell}>
                  {td(paymentMethodsText(method))}
                </div>
                <div className={styles.cell}>
                  {[RoleEnum.admin, RoleEnum.engineer].includes(
                    account.role,
                  ) && (
                    <Button
                      label={t(enabled ? 'general.delete' : 'general.add')}
                      onClick={() => {
                        setConfirmModal({
                          method,
                          enabled,
                        });
                      }}
                      disabled={disabled}
                      design={enabled ? 'pink' : 'white'}
                      autoSize
                      className={styles.tableButton}
                    />
                  )}
                </div>
              </Fragment>
            ))}
          </div>
          {(!company || !headset) && (
            <div className={styles.loading}>
              <div className={styles.loader} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeadsetPayments;
